import React, { useState, useEffect, useRef } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import {
  GlobalActivityCategory,
  GlobalActivityType,
  ActivityVariableDefinition,
} from "../types/activity";
import { globalActivityService } from "../services/globalActivityService";
import { UNIT_OPTIONS, DEFAULT_UNITS } from "../utils/unitUtils";
import { ArrowUpTrayIcon, ChevronDownIcon, ChevronRightIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const SUB_CATEGORY_OPTIONS: Record<string, string[]> = {
    "Rehabilitation (Upper Limb) Exercises": [
        "Shoulder / Rotator Cuff",
        "Scapular Control",
        "Elbow",
        "Wrist",
        "Hand / Finger",
        "Neural Mobilisation",
        "Shoulder - Instability / Proprioception",
        "Shoulder - Return to Throwing / Overhead",
        "Elbow - Tendinopathy Protocols",
        "Elbow - Joint Mobility",
        "Wrist / Hand - Tendon Loading",
        "Thumb / Finger - Specific",
        "Thoracic Outlet / Postural",
        "AC / SC Joint",
        "Proprioception / Neuromuscular",
        "Return to Sport / Work",
        "Aquatic / Offloaded",
    ],
    "Rehabilitation (Lower Limb) Exercises": [
        "Hip - Glute / Abductor",
        "Hip - Flexor / Adductor",
        "Hip - Femoroacetabular",
        "Hamstring / Posterior Thigh",
        "Groin / Adductor",
        "Knee - Quadriceps",
        "Knee - Joint Mobility",
        "Knee - Patellofemoral",
        "Lower Leg",
        "Ankle / Foot",
        "Tendon Loading Protocols",
        "Neural Mobilisation",
        "Balance / Proprioception",
        "Running / Gait Retraining",
        "Return to Sport",
        "Aquatic / Offloaded",
    ],
    "Rehabilitation (Spinal) Exercises": [
        "Cervical",
        "Thoracic",
        "Lumbar",
        "General Spinal Stability",
        "Breathing",
        "Balance / Proprioception",
        "Cervical - Neural",
        "Cervical - Headache",
        "Lumbar - Neural",
        "Lumbar - Stenosis",
        "Lumbar - Disc",
        "Sacroiliac Joint",
        "Return to Sport / Work",
        "Postural / Ergonomic",
    ],
    "Mobility": [
        "Hip",
        "Ankle / Foot",
        "Thoracic / Spine",
        "Shoulder",
        "Full Body",
        "Lower Limb",
        "Upper Limb",
        "Trunk / Spine",
    ],
    "Core": [
        "Anti-Extension",
        "Anti-Rotation",
        "Anti-Lateral Flexion",
        "Flexion",
        "Extension",
        "Carries",
        "Gymnastics / Skill",
        "Foot",
    ],
    "Plyometrics": [
        "Jumps (Bilateral)",
        "Jumps (Unilateral)",
        "Bounds",
        "Hops",
        "Upper Body",
        "Sprint / Acceleration",
        "Rotational",
    ],
    "Gym - Power Exercises": [
        "Olympic Lifts",
        "Jumps (Bilateral)",
        "Jumps (Unilateral)",
        "Upper Body",
        "Hip Extension / Posterior Chain",
        "Rotational",
        "Sprint / Acceleration",
    ],
    "Gym - Strength Exercises": [
        "Chest",
        "Shoulders",
        "Upper Back",
        "Lower Back / Glutes",
        "Glutes / Hamstrings",
        "Quads",
        "Knee-Dominant Unilateral",
        "Adductors / Abductors",
        "Lower Leg / Ankle",
        "Arms",
        "Core / Trunk",
        "Neck",
        "Carries",
    ],
};

// Available preset variable names
const PRESET_VARIABLES: string[] = [
    "Distance",
    "Incline",
    "Intensity",
    "Reps",
    "Rest",
    "Side",
    "Speed",
    "Time",
    "Weight",
];

const GlobalActivitiesPage: React.FC = () => {
    // State for categories
    const [categories, setCategories] = useState<GlobalActivityCategory[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set()
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Activity types per category
    const [activityTypesByCategory, setActivityTypesByCategory] = useState<
        Record<string, GlobalActivityType[]>
    >({});

    // Sub-category filter per category: "all" | "with" | "without"
    const [subCategoryFilter, setSubCategoryFilter] = useState<Record<string, "all" | "with" | "without">>({});

    const cycleSubCategoryFilter = (categoryId: string) => {
        setSubCategoryFilter((prev) => {
            const current = prev[categoryId] || "all";
            const next = current === "all" ? "without" : current === "without" ? "with" : "all";
            return { ...prev, [categoryId]: next };
        });
    };

    const getFilteredActivityTypes = (categoryId: string) => {
        const types = activityTypesByCategory[categoryId] || [];
        const filter = subCategoryFilter[categoryId] || "all";
        if (filter === "with") return types.filter((t) => t.subCategories && t.subCategories.length > 0);
        if (filter === "without") return types.filter((t) => !t.subCategories || t.subCategories.length === 0);
        return types;
    };

    // Category form state
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [editingCategory, setEditingCategory] = useState<string | null>(null);
    const [categoryForm, setCategoryForm] = useState<{
        name: string;
        description: string;
    }>({
        name: "",
        description: "",
    });

    // Activity type form state
    const [isAddingActivityType, setIsAddingActivityType] = useState<
        string | null
    >(null);
    const [editingActivityType, setEditingActivityType] = useState<{
        categoryId: string;
        typeId: string;
    } | null>(null);
    const [activityTypeForm, setActivityTypeForm] = useState<{
        name: string;
        description: string;
        variables: ActivityVariableDefinition[];
        image: string;
        videoUrl: string;
        subCategories: string[];
    }>({
        name: "",
        description: "",
        variables: [],
        image: "",
        videoUrl: "",
        subCategories: [],
    });

    // Bulk selection state
    const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(new Set());
    const [isBulkSubCategoryModalOpen, setIsBulkSubCategoryModalOpen] = useState(false);
    const [bulkSubCategories, setBulkSubCategories] = useState<string[]>([]);

    // Custom variable state
    const [showCustomVariableInput, setShowCustomVariableInput] = useState(false);
    const [customVariableName, setCustomVariableName] = useState("");
    const [customVariableUnit, setCustomVariableUnit] = useState("units");
    const [customVariableUnitsList, setCustomVariableUnitsList] = useState<
        string[]
    >([]);

    // CSV upload state
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [csvUploadCategoryId, setCsvUploadCategoryId] = useState<string | null>(null);
    const [isProcessingCsv, setIsProcessingCsv] = useState(false);
    const [csvProgress, setCsvProgress] = useState<{
        current: number;
        total: number;
        currentName: string;
        errors: string[];
    }>({ current: 0, total: 0, currentName: "", errors: [] });

    // Weight migration state
    const [isRunningMigration, setIsRunningMigration] = useState(false);
    const [migrationProgress, setMigrationProgress] = useState<{
        current: number;
        total: number;
        currentName: string;
        errors: string[];
    }>({ current: 0, total: 0, currentName: "", errors: [] });

    // CSV update state
    const updateFileInputRef = useRef<HTMLInputElement>(null);
    const [isPreparingCsvUpdate, setIsPreparingCsvUpdate] = useState(false);
    const [csvUpdatePreview, setCsvUpdatePreview] = useState<{
        diffs: Array<{
            name: string;
            categoryId: string;
            typeId: string;
            oldDescription: string;
            newDescription: string;
            descriptionChanged: boolean;
            oldVariables: ActivityVariableDefinition[];
            newVariables: ActivityVariableDefinition[];
            variablesChanged: boolean;
        }>;
        notFound: string[];
    } | null>(null);
    const [isUpdatingFromCsv, setIsUpdatingFromCsv] = useState(false);
    const [csvUpdateProgress, setCsvUpdateProgress] = useState<{
        current: number;
        total: number;
        currentName: string;
        errors: string[];
    }>({ current: 0, total: 0, currentName: "", errors: [] });

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
        setLoading(true);
        setError(null);
        const fetchedCategories = await globalActivityService.fetchCategories();
        setCategories(fetchedCategories);
        } catch (err) {
        console.error("Error fetching categories:", err);
        setError(
            err instanceof Error ? err.message : "Failed to fetch categories"
        );
        } finally {
        setLoading(false);
        }
    };

    const fetchActivityTypes = async (categoryId: string) => {
        try {
        const types = await globalActivityService.fetchActivityTypes(categoryId);
        setActivityTypesByCategory((prev) => ({
            ...prev,
            [categoryId]: types,
        }));
        } catch (err) {
        console.error("Error fetching activity types:", err);
        }
    };

    const toggleCategoryExpansion = (categoryId: string) => {
        setExpandedCategories((prev) => {
        const newSet = new Set(Array.from(prev));
        if (newSet.has(categoryId)) {
            newSet.delete(categoryId);
        } else {
            newSet.add(categoryId);
            // Fetch activity types when expanding
            if (!activityTypesByCategory[categoryId]) {
            fetchActivityTypes(categoryId);
            }
        }
        return newSet;
        });
    };

    // Category operations
    const handleAddCategory = async () => {
        if (!categoryForm.name.trim()) return;

        try {
        await globalActivityService.createCategory({
            name: categoryForm.name,
            description: categoryForm.description,
        });
        setCategoryForm({ name: "", description: "" });
        setIsAddingCategory(false);
        fetchCategories();
        } catch (err) {
        console.error("Error creating category:", err);
        setError(err instanceof Error ? err.message : "Failed to create category");
        }
    };

    const handleUpdateCategory = async (categoryId: string) => {
        if (!categoryForm.name.trim()) return;

        try {
        await globalActivityService.updateCategory(categoryId, {
            name: categoryForm.name,
            description: categoryForm.description,
        });
        setCategoryForm({ name: "", description: "" });
        setEditingCategory(null);
        fetchCategories();
        } catch (err) {
        console.error("Error updating category:", err);
        setError(err instanceof Error ? err.message : "Failed to update category");
        }
    };

    const handleDeleteCategory = async (categoryId: string) => {
        if (
        !window.confirm(
            "Are you sure you want to delete this category? All activity types within will also be deleted."
        )
        ) {
        return;
        }

        try {
        await globalActivityService.deleteCategory(categoryId);
        fetchCategories();
        } catch (err) {
        console.error("Error deleting category:", err);
        setError(err instanceof Error ? err.message : "Failed to delete category");
        }
    };

    const startEditingCategory = (category: GlobalActivityCategory) => {
        setEditingCategory(category.id || null);
        setCategoryForm({
        name: category.name,
        description: category.description || "",
        });
    };

    // Activity Type operations
    const handleAddActivityType = async (categoryId: string) => {
        if (
        !activityTypeForm.name.trim() ||
        activityTypeForm.variables.length === 0
        ) {
        setError("Name and at least one variable are required");
        return;
        }

        try {
        await globalActivityService.createActivityType(categoryId, {
            name: activityTypeForm.name,
            description: activityTypeForm.description,
            variables: activityTypeForm.variables,
            image: activityTypeForm.image,
            videoUrl: activityTypeForm.videoUrl,
            subCategories: activityTypeForm.subCategories,
        });
        resetActivityTypeForm();
        setIsAddingActivityType(null);
        fetchActivityTypes(categoryId);
        } catch (err) {
        console.error("Error creating activity type:", err);
        setError(
            err instanceof Error ? err.message : "Failed to create activity type"
        );
        }
    };

    const handleUpdateActivityType = async (
        categoryId: string,
        typeId: string
    ) => {
        if (
        !activityTypeForm.name.trim() ||
        activityTypeForm.variables.length === 0
        ) {
        setError("Name and at least one variable are required");
        return;
        }

        try {
        await globalActivityService.updateActivityType(categoryId, typeId, {
            name: activityTypeForm.name,
            description: activityTypeForm.description,
            variables: activityTypeForm.variables,
            image: activityTypeForm.image,
            videoUrl: activityTypeForm.videoUrl,
            subCategories: activityTypeForm.subCategories,
        });
        resetActivityTypeForm();
        setEditingActivityType(null);
        fetchActivityTypes(categoryId);
        } catch (err) {
        console.error("Error updating activity type:", err);
        setError(
            err instanceof Error ? err.message : "Failed to update activity type"
        );
        }
    };

    const handleDeleteActivityType = async (
        categoryId: string,
        typeId: string
    ) => {
        if (!window.confirm("Are you sure you want to delete this activity type?")) {
        return;
        }

        try {
        await globalActivityService.deleteActivityType(categoryId, typeId);
        fetchActivityTypes(categoryId);
        } catch (err) {
        console.error("Error deleting activity type:", err);
        setError(
            err instanceof Error ? err.message : "Failed to delete activity type"
        );
        }
    };

    const startEditingActivityType = (
        categoryId: string,
        activityType: GlobalActivityType
    ) => {
        setEditingActivityType({
        categoryId,
        typeId: activityType.id || "",
        });
        setActivityTypeForm({
        name: activityType.name,
        description: activityType.description,
        variables: activityType.variables as ActivityVariableDefinition[],
        image: activityType.image || "",
        videoUrl: activityType.videoUrl || "",
        subCategories: activityType.subCategories || [],
        });
        setIsAddingActivityType(null);
    };

    const resetActivityTypeForm = () => {
        setActivityTypeForm({
        name: "",
        description: "",
        variables: [],
        image: "",
        videoUrl: "",
        subCategories: [],
        });
        setShowCustomVariableInput(false);
        setCustomVariableName("");
        setCustomVariableUnit("units");
        setCustomVariableUnitsList([]);
    };

    // CSV Upload Functions
    
    // Helper to find preset variable name with case-insensitive matching
    const findPresetVariableName = (name: string): string | null => {
        const lowerName = name.toLowerCase();
        const preset = PRESET_VARIABLES.find((v) => v.toLowerCase() === lowerName);
        return preset || null;
    };

    const parseVariablesFromCsv = (variablesStr: string): ActivityVariableDefinition[] => {
        // Parse format like: "weight (kg, lbs), Reps (number), Sets (number), Rest (mins), Side (left/right/both)"
        const variables: ActivityVariableDefinition[] = [];
        
        // Split by comma but respect parentheses
        const parts: string[] = [];
        let current = "";
        let parenDepth = 0;
        
        for (const char of variablesStr) {
        if (char === "(") parenDepth++;
        if (char === ")") parenDepth--;
        if (char === "," && parenDepth === 0) {
            parts.push(current.trim());
            current = "";
        } else {
            current += char;
        }
        }
        if (current.trim()) parts.push(current.trim());

        for (const part of parts) {
        // Parse "variableName (unit1, unit2)" or "variableName (unit1/unit2/unit3)" format
        const match = part.match(/^([^(]+)\s*\(([^)]+)\)$/);
        if (match) {
            let name = match[1].trim();
            const unitsStr = match[2];
            
            // Split units by comma OR slash
            const unitsList = unitsStr
            .split(/[,\/]/)
            .map((u) => u.trim())
            .filter(Boolean);
            const defaultUnit = unitsList[0] || "units";
            
            // Check if this matches a preset variable (case-insensitive)
            const presetName = findPresetVariableName(name);
            if (presetName) {
            name = presetName; // Use the properly cased preset name
            }
            
            variables.push({
            name,
            defaultUnit,
            unitsList: unitsList.length > 1 ? unitsList : undefined,
            });
        } else {
            // No units specified, just use the name
            let name = part.trim();
            if (name) {
            // Check if this matches a preset variable (case-insensitive)
            const presetName = findPresetVariableName(name);
            if (presetName) {
                name = presetName;
                // Use default unit from preset
                const defaultUnit = DEFAULT_UNITS[presetName] || "units";
                variables.push({
                name,
                defaultUnit,
                });
            } else {
                variables.push({
                name,
                defaultUnit: "units",
                });
            }
            }
        }
        }

        return variables;
    };

    const parseCsvContent = (csvText: string): Array<{
        name: string;
        description: string;
        variables: ActivityVariableDefinition[];
        subCategories: string[];
    }> => {
        const lines = csvText.split("\n").map((line) => line.trim()).filter((line) => line);

        // Skip header row
        const dataLines = lines.slice(1);
        const activities: Array<{
        name: string;
        description: string;
        variables: ActivityVariableDefinition[];
        subCategories: string[];
        }> = [];

        for (const line of dataLines) {
        // Parse CSV line handling quoted fields
        const fields: string[] = [];
        let currentField = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
            inQuotes = !inQuotes;
            } else if (char === "," && !inQuotes) {
            fields.push(currentField.trim());
            currentField = "";
            } else {
            currentField += char;
            }
        }
        fields.push(currentField.trim());

        // fields[0] = Exercise Name
        // fields[1] = Description
        // fields[2] = Variables
        // fields[3] = Notes (ignored)
        // fields[4] = Sub-categories (semicolon-separated)

        const name = fields[0] || "";
        const description = fields[1] || "";
        const variablesStr = fields[2] || "";
        const subCategories = fields[4]
            ? fields[4].split(";").map((s) => s.trim()).filter(Boolean)
            : [];

        if (name) {
            activities.push({
            name,
            description,
            variables: parseVariablesFromCsv(variablesStr),
            subCategories,
            });
        }
        }

        return activities;
    };

    const handleCsvUpload = async (categoryId: string, file: File) => {
        try {
        setIsProcessingCsv(true);
        setCsvProgress({ current: 0, total: 0, currentName: "", errors: [] });

        const text = await file.text();
        const activities = parseCsvContent(text);

        if (activities.length === 0) {
            setError("No valid activities found in CSV file.");
            setIsProcessingCsv(false);
            return;
        }

        setCsvProgress((prev) => ({ ...prev, total: activities.length }));

        const errors: string[] = [];

        for (let i = 0; i < activities.length; i++) {
            const activity = activities[i];
            setCsvProgress((prev) => ({
            ...prev,
            current: i + 1,
            currentName: activity.name,
            }));

            try {
            // Validate activity has at least one variable
            if (activity.variables.length === 0) {
                errors.push(`${activity.name}: No variables specified`);
                continue;
            }

            await globalActivityService.createActivityType(categoryId, {
                name: activity.name,
                description: activity.description,
                variables: activity.variables,
                subCategories: activity.subCategories,
            });

            // Small delay to prevent overwhelming the server
            await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (err) {
            errors.push(
                `${activity.name}: ${err instanceof Error ? err.message : "Failed to create"}`
            );
            }
        }

        setCsvProgress((prev) => ({ ...prev, errors }));

        // Refresh activity types
        await fetchActivityTypes(categoryId);

        // Show completion
        setTimeout(() => {
            setIsProcessingCsv(false);
            if (errors.length > 0) {
            setError(`Upload completed with ${errors.length} error(s). Check console for details.`);
            console.error("CSV upload errors:", errors);
            }
        }, 1000);
        } catch (err) {
        console.error("Error processing CSV:", err);
        setError("Error processing CSV file. Please check the format and try again.");
        setIsProcessingCsv(false);
        }
    };

    const triggerCsvUpload = (categoryId: string) => {
        setCsvUploadCategoryId(categoryId);
        if (fileInputRef.current) {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && csvUploadCategoryId) {
        if (file.type === "text/csv" || file.name.endsWith(".csv")) {
            handleCsvUpload(csvUploadCategoryId, file);
        } else {
            setError("Please select a valid CSV file.");
        }
        }
        setCsvUploadCategoryId(null);
    };

    const parseUpdateCsvContent = (csvText: string): Array<{
        name: string;
        description: string;
        variables: ActivityVariableDefinition[];
    }> => {
        const lines = csvText.split("\n").map((line) => line.trim()).filter((line) => line);
        if (lines.length < 2) return [];

        // Detect delimiter: tab-separated or comma-separated
        const delimiter = lines[0].includes("\t") ? "\t" : ",";
        const dataLines = lines.slice(1);

        const parseLine = (line: string): string[] => {
            if (delimiter === "\t") return line.split("\t").map((f) => f.trim());
            const fields: string[] = [];
            let current = "";
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') { inQuotes = !inQuotes; }
                else if (char === "," && !inQuotes) { fields.push(current.trim()); current = ""; }
                else { current += char; }
            }
            fields.push(current.trim());
            return fields;
        };

        return dataLines
            .map((line) => {
                const fields = parseLine(line);
                const name = fields[0]?.trim() || "";
                const description = fields[1]?.trim() || "";
                const variablesStr = fields[2]?.trim() || "";
                if (!name) return null;
                return { name, description, variables: parseVariablesFromCsv(variablesStr) };
            })
            .filter(Boolean) as Array<{ name: string; description: string; variables: ActivityVariableDefinition[] }>;
    };

    const handleCsvUpdateFileSelected = async (file: File) => {
        try {
            setIsPreparingCsvUpdate(true);

            const text = await file.text();
            const csvActivities = parseUpdateCsvContent(text);

            if (csvActivities.length === 0) {
                setError("No valid activities found in CSV file.");
                setIsPreparingCsvUpdate(false);
                return;
            }

            const allActivityTypes = await globalActivityService.fetchAllActivityTypes();
            const nameMap = new Map<string, GlobalActivityType>();
            for (const activity of allActivityTypes) {
                if (activity.id && activity.categoryId) {
                    nameMap.set(activity.name.trim().toLowerCase(), activity);
                }
            }

            const serializeVars = (vars: ActivityVariableDefinition[]) =>
                vars.map((v) => `${v.name}|${v.defaultUnit}`).join(",");

            const diffs: NonNullable<typeof csvUpdatePreview>["diffs"] = [];
            const notFound: string[] = [];

            for (const csvActivity of csvActivities) {
                const existing = nameMap.get(csvActivity.name.trim().toLowerCase());
                if (!existing || !existing.id || !existing.categoryId) {
                    notFound.push(csvActivity.name);
                    continue;
                }
                const oldDescription = existing.description || "";
                const newDescription = csvActivity.description;
                const descriptionChanged = oldDescription !== newDescription;
                const oldVariables = (existing.variables || []) as ActivityVariableDefinition[];
                const newVariables = csvActivity.variables;
                const variablesChanged = serializeVars(oldVariables) !== serializeVars(newVariables);

                if (descriptionChanged || variablesChanged) {
                    diffs.push({
                        name: csvActivity.name,
                        categoryId: existing.categoryId,
                        typeId: existing.id,
                        oldDescription,
                        newDescription,
                        descriptionChanged,
                        oldVariables,
                        newVariables,
                        variablesChanged,
                    });
                }
            }

            setCsvUpdatePreview({ diffs, notFound });
            setIsPreparingCsvUpdate(false);
        } catch (err) {
            console.error("Error preparing CSV update:", err);
            setError("Error processing CSV file. Please check the format and try again.");
            setIsPreparingCsvUpdate(false);
        }
    };

    const applyCsvUpdate = async () => {
        if (!csvUpdatePreview) return;
        const { diffs } = csvUpdatePreview;
        setCsvUpdatePreview(null);
        setIsUpdatingFromCsv(true);
        setCsvUpdateProgress({ current: 0, total: diffs.length, currentName: "", errors: [] });

        const errors: string[] = [];
        const affectedCategoryIds = new Set<string>();

        for (let i = 0; i < diffs.length; i++) {
            const diff = diffs[i];
            setCsvUpdateProgress((prev) => ({ ...prev, current: i + 1, currentName: diff.name }));
            try {
                if (diff.newVariables.length === 0) {
                    errors.push(`${diff.name}: No variables specified`);
                    continue;
                }
                await globalActivityService.updateActivityType(diff.categoryId, diff.typeId, {
                    description: diff.newDescription,
                    variables: diff.newVariables,
                });
                affectedCategoryIds.add(diff.categoryId);
                await new Promise((resolve) => setTimeout(resolve, 50));
            } catch (err) {
                errors.push(`${diff.name}: ${err instanceof Error ? err.message : "Failed to update"}`);
            }
        }

        setCsvUpdateProgress((prev) => ({ ...prev, errors }));
        await Promise.all(Array.from(affectedCategoryIds).map((cId) => fetchActivityTypes(cId)));

        setTimeout(() => {
            setIsUpdatingFromCsv(false);
            if (errors.length > 0) {
                setError(`Update completed with ${errors.length} error(s).`);
            }
        }, 1500);
    };

    const triggerCsvUpdate = () => {
        if (updateFileInputRef.current) {
            updateFileInputRef.current.value = "";
            updateFileInputRef.current.click();
        }
    };

    const handleUpdateFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type === "text/csv" || file.name.endsWith(".csv")) {
                handleCsvUpdateFileSelected(file);
            } else {
                setError("Please select a valid CSV file.");
            }
        }
    };

    const migrateWeightVariables = async () => {
        const allActivities = await globalActivityService.fetchAllActivityTypes();

        const toMigrate = allActivities.filter((activity) => {
            const vars = (activity.variables || []) as ActivityVariableDefinition[];
            return vars.some((v) => v.name === "Set Weight" || v.name === "Achieved Weight");
        });

        if (toMigrate.length === 0) {
            window.alert("No activities found with Set Weight or Achieved Weight variables.");
            return;
        }

        if (!window.confirm(
            `Found ${toMigrate.length} activities with Set Weight or Achieved Weight variables.\n\nThese will be replaced with a single Weight variable (kg/lbs/g). Continue?`
        )) return;

        setIsRunningMigration(true);
        setMigrationProgress({ current: 0, total: toMigrate.length, currentName: "", errors: [] });

        const errors: string[] = [];

        for (let i = 0; i < toMigrate.length; i++) {
            const activity = toMigrate[i];
            setMigrationProgress((prev) => ({ ...prev, current: i + 1, currentName: activity.name }));

            try {
                const vars = (activity.variables || []) as ActivityVariableDefinition[];
                const filtered = vars.filter((v) => v.name !== "Set Weight" && v.name !== "Achieved Weight");
                const hasWeight = filtered.some((v) => v.name === "Weight");
                const newVars: ActivityVariableDefinition[] = hasWeight
                    ? filtered
                    : [...filtered, { name: "Weight", defaultUnit: "kg", unitsList: ["kg", "lbs", "g"] }];

                await globalActivityService.updateActivityType(activity.categoryId!, activity.id!, {
                    variables: newVars,
                });
                await new Promise((r) => setTimeout(r, 50));
            } catch (err) {
                errors.push(`${activity.name}: ${err instanceof Error ? err.message : "Failed"}`);
            }
        }

        setMigrationProgress((prev) => ({ ...prev, errors }));
        setTimeout(() => {
            setIsRunningMigration(false);
            if (errors.length > 0) {
                setError(`Migration completed with ${errors.length} error(s).`);
            }
        }, 1500);
    };

    // Variable operations
    const toggleVariable = (variableName: string) => {
        setActivityTypeForm((prev) => {
        const existingIndex = prev.variables.findIndex(
            (v) => v.name === variableName
        );

        if (existingIndex !== -1) {
            return {
            ...prev,
            variables: prev.variables.filter((_, idx) => idx !== existingIndex),
            };
        } else {
            const defaultUnit = DEFAULT_UNITS[variableName] || "units";
            const unitsList = UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
            return {
            ...prev,
            variables: [
                ...prev.variables,
                { name: variableName, defaultUnit, unitsList },
            ],
            };
        }
        });
    };

    const updateVariableUnit = (variableName: string, newUnit: string) => {
        setActivityTypeForm((prev) => ({
        ...prev,
        variables: prev.variables.map((v) =>
            v.name === variableName ? { ...v, defaultUnit: newUnit } : v
        ),
        }));
    };

    const updateVariableUnitsList = (variableName: string, unitsList: string[]) => {
        setActivityTypeForm((prev) => ({
        ...prev,
        variables: prev.variables.map((v) =>
            v.name === variableName ? { ...v, unitsList } : v
        ),
        }));
    };

    const isVariableSelected = (variableName: string): boolean => {
        return activityTypeForm.variables.some((v) => v.name === variableName);
    };

    const getVariableUnit = (variableName: string): string => {
        const variable = activityTypeForm.variables.find(
        (v) => v.name === variableName
        );
        return variable?.defaultUnit || DEFAULT_UNITS[variableName] || "units";
    };

    const getVariableUnitsList = (variableName: string): string[] => {
        const variable = activityTypeForm.variables.find(
        (v) => v.name === variableName
        );
        return variable?.unitsList || UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
    };

    const addCustomVariable = () => {
        const trimmedName = customVariableName.trim();
        if (!trimmedName) return;

        // Check if already exists
        const existingNames = activityTypeForm.variables.map((v) =>
        v.name.toLowerCase()
        );
        if (
        existingNames.includes(trimmedName.toLowerCase()) ||
        PRESET_VARIABLES.map((v) => v.toLowerCase()).includes(
            trimmedName.toLowerCase()
        )
        ) {
        setError("This variable already exists");
        return;
        }

        const unitsList =
        customVariableUnitsList.length > 0
            ? customVariableUnitsList
            : [customVariableUnit];

        setActivityTypeForm((prev) => ({
        ...prev,
        variables: [
            ...prev.variables,
            { name: trimmedName, defaultUnit: customVariableUnit, unitsList },
        ],
        }));

        setCustomVariableName("");
        setCustomVariableUnit("units");
        setCustomVariableUnitsList([]);
        setShowCustomVariableInput(false);
        setError(null);
    };

    const addUnitToCustomList = (unit: string) => {
        const trimmed = unit.trim();
        if (trimmed && !customVariableUnitsList.includes(trimmed)) {
        setCustomVariableUnitsList((prev) => [...prev, trimmed]);
        }
    };

    const removeUnitFromCustomList = (unit: string) => {
        setCustomVariableUnitsList((prev) => prev.filter((u) => u !== unit));
    };

    // Bulk selection helpers
    const activityKey = (categoryId: string, typeId: string) => `${categoryId}::${typeId}`;

    const toggleActivitySelection = (categoryId: string, typeId: string) => {
        const key = activityKey(categoryId, typeId);
        setSelectedActivityIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const isActivitySelected = (categoryId: string, typeId: string) =>
        selectedActivityIds.has(activityKey(categoryId, typeId));

    const allVisibleIds = (categoryId: string): string[] =>
        (activityTypesByCategory[categoryId] || [])
            .filter((t) => t.id)
            .map((t) => activityKey(categoryId, t.id!));

    const toggleSelectAllInCategory = (categoryId: string) => {
        const ids = allVisibleIds(categoryId);
        const allSelected = ids.every((k) => selectedActivityIds.has(k));
        setSelectedActivityIds((prev) => {
            const next = new Set(prev);
            if (allSelected) ids.forEach((k) => next.delete(k));
            else ids.forEach((k) => next.add(k));
            return next;
        });
    };

    const isCategoryAllSelected = (categoryId: string) => {
        const ids = allVisibleIds(categoryId);
        return ids.length > 0 && ids.every((k) => selectedActivityIds.has(k));
    };

    const isCategoryPartiallySelected = (categoryId: string) => {
        const ids = allVisibleIds(categoryId);
        return ids.some((k) => selectedActivityIds.has(k)) && !isCategoryAllSelected(categoryId);
    };

    const handleBulkUpdateSubCategories = async () => {
        try {
            const updates = Array.from(selectedActivityIds).map((key) => {
                const [categoryId, typeId] = key.split("::");
                return globalActivityService.updateActivityType(categoryId, typeId, {
                    subCategories: bulkSubCategories,
                });
            });
            await Promise.all(updates);

            const affectedCategoryIds = new Set(
                Array.from(selectedActivityIds).map((key) => key.split("::")[0])
            );
            await Promise.all(Array.from(affectedCategoryIds).map((cId) => fetchActivityTypes(cId)));

            setSelectedActivityIds(new Set());
            setIsBulkSubCategoryModalOpen(false);
            setBulkSubCategories([]);
            setBulkSubCategories([]);
        } catch (err) {
            console.error("Error bulk updating sub-categories:", err);
            setError(err instanceof Error ? err.message : "Failed to bulk update sub-categories");
        }
    };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
                {/* Hidden file inputs for CSV operations */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                />
                <input
                    ref={updateFileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleUpdateFileInputChange}
                />

                {/* Bulk Sub-category Modal */}
                {isBulkSubCategoryModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-semibold">Update Sub-categories</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                            Applies to {selectedActivityIds.size} selected {selectedActivityIds.size === 1 ? "activity" : "activities"}
                            </p>
                        </div>
                        <button
                            onClick={() => {
                            setIsBulkSubCategoryModalOpen(false);
                            setBulkSubCategories([]);
                            setBulkSubCategories([]);
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                        </div>

                        <div className="p-4 space-y-3">
                        <p className="text-xs text-gray-500">
                            These sub-categories will replace existing ones on all selected activities.
                        </p>
                        {(() => {
                            const selectedCatIds = new Set(
                                Array.from(selectedActivityIds).map((k) => k.split("::")[0])
                            );
                            const bulkOptions = Array.from(
                                new Set(
                                    categories
                                        .filter((c) => c.id && selectedCatIds.has(c.id))
                                        .flatMap((c) => SUB_CATEGORY_OPTIONS[c.name] || [])
                                )
                            );
                            if (bulkOptions.length === 0) {
                                return (
                                    <p className="text-xs text-gray-400 italic">
                                        No sub-category options available for the selected activities' categories.
                                    </p>
                                );
                            }
                            return (
                                <div className="grid grid-cols-2 gap-2">
                                {bulkOptions.map((option) => {
                                    const checked = bulkSubCategories.includes(option);
                                    return (
                                    <label
                                        key={option}
                                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-colors ${
                                        checked ? "border-gray-900 bg-white" : "border-gray-200 bg-white hover:bg-gray-50"
                                        }`}
                                    >
                                        <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                            setBulkSubCategories((prev) =>
                                            checked ? prev.filter((s) => s !== option) : [...prev, option]
                                            )
                                        }
                                        className="w-3.5 h-3.5 text-black bg-gray-100 border-gray-300 rounded focus:ring-black"
                                        />
                                        {option}
                                    </label>
                                    );
                                })}
                                </div>
                            );
                        })()}
                        </div>

                        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                        <button
                            onClick={() => {
                            setIsBulkSubCategoryModalOpen(false);
                            setBulkSubCategories([]);
                            setBulkSubCategories([]);
                            }}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleBulkUpdateSubCategories}
                            className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors"
                        >
                            Save
                        </button>
                        </div>
                    </div>
                    </div>
                )}

                {/* CSV Update — Preview Dialog */}
                {csvUpdatePreview && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-semibold">Review Changes</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                            {csvUpdatePreview.diffs.length} {csvUpdatePreview.diffs.length === 1 ? "activity" : "activities"} will be updated
                            {csvUpdatePreview.notFound.length > 0 && ` · ${csvUpdatePreview.notFound.length} not found`}
                            </p>
                        </div>
                        <button onClick={() => setCsvUpdatePreview(null)} className="p-1 hover:bg-gray-100 rounded">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {csvUpdatePreview.diffs.length === 0 && csvUpdatePreview.notFound.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-6">No changes detected.</p>
                        )}

                        {csvUpdatePreview.diffs.map((diff) => {
                            const oldVarKeys = new Set(diff.oldVariables.map((v) => `${v.name}|${v.defaultUnit}`));
                            const newVarKeys = new Set(diff.newVariables.map((v) => `${v.name}|${v.defaultUnit}`));
                            const removedVars = diff.oldVariables.filter((v) => !newVarKeys.has(`${v.name}|${v.defaultUnit}`));
                            const addedVars = diff.newVariables.filter((v) => !oldVarKeys.has(`${v.name}|${v.defaultUnit}`));
                            const keptVars = diff.newVariables.filter((v) => oldVarKeys.has(`${v.name}|${v.defaultUnit}`));

                            return (
                            <div key={diff.typeId} className="border border-gray-200 rounded-lg p-3 space-y-2">
                                <p className="text-sm font-semibold text-gray-900">{diff.name}</p>

                                {diff.descriptionChanged && (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</p>
                                    {diff.oldDescription && (
                                    <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1 line-through">
                                        {diff.oldDescription.length > 120 ? diff.oldDescription.slice(0, 120) + "…" : diff.oldDescription}
                                    </p>
                                    )}
                                    <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                                    {diff.newDescription.length > 120 ? diff.newDescription.slice(0, 120) + "…" : diff.newDescription}
                                    </p>
                                </div>
                                )}

                                {diff.variablesChanged && (
                                <div className="space-y-1">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Variables</p>
                                    <div className="flex flex-wrap gap-1">
                                    {keptVars.map((v) => (
                                        <span key={v.name} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                                        {v.name} ({v.defaultUnit})
                                        </span>
                                    ))}
                                    {removedVars.map((v) => (
                                        <span key={v.name} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-600 line-through">
                                        {v.name} ({v.defaultUnit})
                                        </span>
                                    ))}
                                    {addedVars.map((v) => (
                                        <span key={v.name} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                        + {v.name} ({v.defaultUnit})
                                        </span>
                                    ))}
                                    </div>
                                </div>
                                )}
                            </div>
                            );
                        })}

                        {csvUpdatePreview.notFound.length > 0 && (
                            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                            <p className="text-xs font-medium text-amber-700 mb-2">
                                {csvUpdatePreview.notFound.length} {csvUpdatePreview.notFound.length === 1 ? "name" : "names"} not matched in the database:
                            </p>
                            <ul className="space-y-0.5">
                                {csvUpdatePreview.notFound.map((name, i) => (
                                <li key={i} className="text-xs text-amber-600">{name}</li>
                                ))}
                            </ul>
                            </div>
                        )}
                        </div>

                        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                        <button
                            onClick={() => setCsvUpdatePreview(null)}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={applyCsvUpdate}
                            disabled={csvUpdatePreview.diffs.length === 0}
                            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Apply {csvUpdatePreview.diffs.length} {csvUpdatePreview.diffs.length === 1 ? "change" : "changes"}
                        </button>
                        </div>
                    </div>
                    </div>
                )}

                {/* CSV Update — Progress Dialog */}
                {isUpdatingFromCsv && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Updating Activities from CSV</h3>
                        <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{csvUpdateProgress.current} / {csvUpdateProgress.total}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-black h-2 rounded-full transition-all duration-300"
                                style={{ width: `${csvUpdateProgress.total > 0 ? (csvUpdateProgress.current / csvUpdateProgress.total) * 100 : 0}%` }}
                            />
                            </div>
                        </div>
                        {csvUpdateProgress.currentName && (
                            <p className="text-sm text-gray-600">
                            Updating: <span className="font-medium">{csvUpdateProgress.currentName}</span>
                            </p>
                        )}
                        {csvUpdateProgress.errors.length > 0 && (
                            <div className="text-sm text-red-600">
                            <p className="font-medium">{csvUpdateProgress.errors.length} error(s):</p>
                            <ul className="list-disc list-inside mt-1 max-h-24 overflow-y-auto">
                                {csvUpdateProgress.errors.slice(0, 5).map((err, i) => (
                                <li key={i} className="truncate">{err}</li>
                                ))}
                                {csvUpdateProgress.errors.length > 5 && (
                                <li>...and {csvUpdateProgress.errors.length - 5} more</li>
                                )}
                            </ul>
                            </div>
                        )}
                        {csvUpdateProgress.current === csvUpdateProgress.total && csvUpdateProgress.total > 0 && (
                            <p className="text-green-600 font-medium">Update complete!</p>
                        )}
                        </div>
                    </div>
                    </div>
                )}

                {/* Weight Migration Progress Dialog */}
                {isRunningMigration && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Migrating Weight Variables</h3>
                        <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{migrationProgress.current} / {migrationProgress.total}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}%` }}
                            />
                            </div>
                        </div>
                        {migrationProgress.currentName && (
                            <p className="text-sm text-gray-600">
                            Updating: <span className="font-medium">{migrationProgress.currentName}</span>
                            </p>
                        )}
                        {migrationProgress.errors.length > 0 && (
                            <div className="text-sm text-red-600">
                            <p className="font-medium">{migrationProgress.errors.length} error(s):</p>
                            <ul className="list-disc list-inside mt-1 max-h-24 overflow-y-auto">
                                {migrationProgress.errors.slice(0, 5).map((err, i) => (
                                <li key={i} className="truncate">{err}</li>
                                ))}
                                {migrationProgress.errors.length > 5 && (
                                <li>...and {migrationProgress.errors.length - 5} more</li>
                                )}
                            </ul>
                            </div>
                        )}
                        {migrationProgress.current === migrationProgress.total && migrationProgress.total > 0 && (
                            <p className="text-green-600 font-medium">Migration complete!</p>
                        )}
                        </div>
                    </div>
                    </div>
                )}

                {/* CSV Processing Dialog */}
                {isProcessingCsv && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Processing CSV Upload</h3>
                        <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>
                                {csvProgress.current} / {csvProgress.total}
                            </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-black h-2 rounded-full transition-all duration-300"
                                style={{
                                width: `${csvProgress.total > 0 ? (csvProgress.current / csvProgress.total) * 100 : 0}%`,
                                }}
                            />
                            </div>
                        </div>
                        {csvProgress.currentName && (
                            <p className="text-sm text-gray-600">
                            Adding: <span className="font-medium">{csvProgress.currentName}</span>
                            </p>
                        )}
                        {csvProgress.errors.length > 0 && (
                            <div className="text-sm text-red-600">
                            <p className="font-medium">{csvProgress.errors.length} error(s):</p>
                            <ul className="list-disc list-inside mt-1 max-h-32 overflow-y-auto">
                                {csvProgress.errors.slice(0, 5).map((err, i) => (
                                <li key={i} className="truncate">{err}</li>
                                ))}
                                {csvProgress.errors.length > 5 && (
                                <li>...and {csvProgress.errors.length - 5} more</li>
                                )}
                            </ul>
                            </div>
                        )}
                        {csvProgress.current === csvProgress.total && csvProgress.total > 0 && (
                            <p className="text-green-600 font-medium">
                            Upload complete!
                            </p>
                        )}
                        </div>
                    </div>
                    </div>
                )}

                {/* Edit Activity Type Dialog */}
                {editingActivityType && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold">Edit Activity Type</h3>
                        <button
                            onClick={() => {
                            setEditingActivityType(null);
                            resetActivityTypeForm();
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                            Activity Name *
                            </label>
                            <input
                            type="text"
                            placeholder="e.g., 5K Run, Bench Press"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            value={activityTypeForm.name}
                            onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                ...prev,
                                name: e.target.value,
                                }))
                            }
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                            Description
                            </label>
                            <textarea
                            placeholder="Describe this activity..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                            rows={2}
                            value={activityTypeForm.description}
                            onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                ...prev,
                                description: e.target.value,
                                }))
                            }
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">
                            Variables *
                            </label>
                            <p className="text-xs text-gray-400 mb-3">
                            Select which metrics users can track for this activity
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            {PRESET_VARIABLES.map((variableName) => {
                                const selected = isVariableSelected(variableName);
                                const availableUnits =
                                UNIT_OPTIONS[variableName] || UNIT_OPTIONS.Default;
                                const currentUnit = getVariableUnit(variableName);
                                const currentUnitsList = getVariableUnitsList(variableName);

                                return (
                                <div
                                    key={variableName}
                                    className={`p-3 rounded-lg border-2 transition-colors ${
                                    selected
                                        ? "border-gray-900 bg-white"
                                        : "border-gray-200 bg-white"
                                    }`}
                                >
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleVariable(variableName)}
                                        className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black"
                                    />
                                    <span className="text-sm font-medium">
                                        {variableName}
                                    </span>
                                    </label>

                                    {selected && (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                        <label className="text-xs text-gray-600">
                                            Default Unit:
                                        </label>
                                        <select
                                            value={currentUnit}
                                            onChange={(e) =>
                                            updateVariableUnit(variableName, e.target.value)
                                            }
                                            className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-black"
                                        >
                                            {availableUnits.map((unit) => (
                                            <option key={unit} value={unit}>
                                                {unit}
                                            </option>
                                            ))}
                                        </select>
                                        </div>
                                        <div>
                                        <label className="text-xs text-gray-600">
                                            Available Units:
                                        </label>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {availableUnits.map((unit) => (
                                            <label
                                                key={unit}
                                                className="flex items-center gap-1 text-xs"
                                            >
                                                <input
                                                type="checkbox"
                                                checked={currentUnitsList.includes(unit)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                    updateVariableUnitsList(variableName, [
                                                        ...currentUnitsList,
                                                        unit,
                                                    ]);
                                                    } else {
                                                    updateVariableUnitsList(
                                                        variableName,
                                                        currentUnitsList.filter((u) => u !== unit)
                                                    );
                                                    }
                                                }}
                                                className="w-3 h-3"
                                                />
                                                {unit}
                                            </label>
                                            ))}
                                        </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                                );
                            })}
                            </div>

                            {activityTypeForm.variables
                            .filter((v) => !PRESET_VARIABLES.includes(v.name))
                            .map((customVar) => (
                                <div
                                key={customVar.name}
                                className="mb-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50"
                                >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-blue-700">
                                    {customVar.name} (Custom)
                                    </span>
                                    <button
                                    onClick={() => toggleVariable(customVar.name)}
                                    className="p-1 hover:bg-blue-100 rounded"
                                    >
                                    <XMarkIcon className="w-4 h-4 text-blue-600" />
                                    </button>
                                </div>
                                <div className="mt-2 text-xs text-blue-600">
                                    Default: {customVar.defaultUnit}
                                    {customVar.unitsList && customVar.unitsList.length > 0 && (
                                    <span> | Options: {customVar.unitsList.join(", ")}</span>
                                    )}
                                </div>
                                </div>
                            ))}

                            {!showCustomVariableInput ? (
                            <button
                                type="button"
                                onClick={() => setShowCustomVariableInput(true)}
                                className="flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors w-full text-left"
                            >
                                <PlusIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">Add custom variable</span>
                            </button>
                            ) : (
                            <div className="p-4 border border-gray-300 rounded-lg bg-white space-y-3">
                                <div>
                                <label className="text-sm font-medium text-gray-700">
                                    Variable Name
                                </label>
                                <input
                                    type="text"
                                    value={customVariableName}
                                    onChange={(e) => setCustomVariableName(e.target.value)}
                                    placeholder="e.g., Cadence, Power"
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                />
                                </div>
                                <div>
                                <label className="text-sm font-medium text-gray-700">
                                    Default Unit
                                </label>
                                <input
                                    type="text"
                                    value={customVariableUnit}
                                    onChange={(e) => setCustomVariableUnit(e.target.value)}
                                    placeholder="e.g., bpm, watts"
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                />
                                </div>
                                <div className="flex gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={addCustomVariable}
                                    disabled={!customVariableName.trim()}
                                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-black disabled:opacity-50 transition-colors"
                                >
                                    Add Variable
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                    setShowCustomVariableInput(false);
                                    setCustomVariableName("");
                                    setCustomVariableUnit("units");
                                    setCustomVariableUnitsList([]);
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                                >
                                    Cancel
                                </button>
                                </div>
                            </div>
                            )}

                            {activityTypeForm.variables.length === 0 && (
                            <p className="text-xs text-red-500 mt-2">
                                *Please select at least one variable
                            </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                            Image URL (optional)
                            </label>
                            <input
                            type="url"
                            placeholder="https://..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            value={activityTypeForm.image}
                            onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                ...prev,
                                image: e.target.value,
                                }))
                            }
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                            Video URL (optional)
                            </label>
                            <input
                            type="url"
                            placeholder="https://www.youtube.com/watch?v=..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            value={activityTypeForm.videoUrl}
                            onChange={(e) =>
                                setActivityTypeForm((prev) => ({
                                ...prev,
                                videoUrl: e.target.value,
                                }))
                            }
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">
                            Sub-categories (optional)
                            </label>
                            {(() => {
                            const catName = categories.find((c) => c.id === editingActivityType?.categoryId)?.name ?? "";
                            const opts = SUB_CATEGORY_OPTIONS[catName] || [];
                            if (opts.length === 0) return null;
                            return (
                                <div className="grid grid-cols-2 gap-2">
                                {opts.map((option) => {
                                    const checked = activityTypeForm.subCategories.includes(option);
                                    return (
                                    <label
                                        key={option}
                                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-colors ${
                                        checked ? "border-gray-900 bg-white" : "border-gray-200 bg-white hover:bg-gray-50"
                                        }`}
                                    >
                                        <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                            setActivityTypeForm((prev) => ({
                                            ...prev,
                                            subCategories: checked
                                                ? prev.subCategories.filter((s) => s !== option)
                                                : [...prev.subCategories, option],
                                            }))
                                        }
                                        className="w-3.5 h-3.5 text-black bg-gray-100 border-gray-300 rounded focus:ring-black"
                                        />
                                        {option}
                                    </label>
                                    );
                                })}
                                </div>
                            );
                            })()}
                        </div>
                        </div>

                        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                        <button
                            onClick={() => {
                            setEditingActivityType(null);
                            resetActivityTypeForm();
                            }}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                            handleUpdateActivityType(
                                editingActivityType.categoryId,
                                editingActivityType.typeId
                            );
                            }}
                            disabled={
                            !activityTypeForm.name.trim() ||
                            activityTypeForm.variables.length === 0
                            }
                            className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Update Activity
                        </button>
                        </div>
                    </div>
                    </div>
                )}

                <div className="flex items-start justify-between gap-4">
                    <h1 className="text-2xl font-heading">Global Activities</h1>
                    <div className="flex items-center gap-2">
                        {selectedActivityIds.size > 0 && (
                            <>
                                <button
                                    onClick={() => setIsBulkSubCategoryModalOpen(true)}
                                    className="btn btn-sm !h-10 gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                                >
                                    Update Sub-categories ({selectedActivityIds.size})
                                </button>
                                <button
                                    onClick={() => setSelectedActivityIds(new Set())}
                                    className="btn btn-sm !h-10 gap-2 bg-white text-gray-500 hover:bg-gray-100 border border-gray-300"
                                    title="Clear selection"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                    Clear
                                </button>
                            </>
                        )}
                        <button
                            onClick={migrateWeightVariables}
                            disabled={isRunningMigration}
                            className="btn btn-sm !h-10 gap-2 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="One-time migration: replace Set Weight / Achieved Weight with Weight"
                        >
                            Migrate Weight Variables
                        </button>
                        <button
                            onClick={triggerCsvUpdate}
                            disabled={isUpdatingFromCsv || isPreparingCsvUpdate || !!csvUpdatePreview}
                            className="btn btn-sm !h-10 gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Update existing activities from a CSV (Activity Name, Description, Variables)"
                        >
                            <ArrowUpTrayIcon className="w-4 h-4" />
                            Update from CSV
                        </button>
                        <button
                            onClick={() => setIsAddingCategory(true)}
                            className="btn btn-primary btn-sm !h-10 gap-2"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Category
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                    </div>
                )}

                {/* Add Category Form */}
                {isAddingCategory && (
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium mb-4">Add New Category</h3>
                    <div className="space-y-4">
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name *
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., Running, Strength Training"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                            value={categoryForm.name}
                            onChange={(e) =>
                            setCategoryForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                        />
                        </div>
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            placeholder="Describe this category..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                            rows={2}
                            value={categoryForm.description}
                            onChange={(e) =>
                            setCategoryForm((prev) => ({
                                ...prev,
                                description: e.target.value,
                            }))
                            }
                        />
                        </div>
                    </div>
                    <div className="flex justify-between gap-3 mt-4">
                        <button
                        onClick={() => {
                            setIsAddingCategory(false);
                            setCategoryForm({ name: "", description: "" });
                        }}
                        className="bg-gray-200 py-3 px-6 rounded-md text-gray-700 hover:bg-gray-300"
                        >
                        Cancel
                        </button>
                        <button
                        onClick={handleAddCategory}
                        disabled={!categoryForm.name.trim()}
                        className="bg-gray-700 py-3 px-6 rounded-md text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                        Add Category
                        </button>
                    </div>
                    </div>
                )}

                {/* Categories List */}
                <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
                    {loading ? (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                        Loading global activities...
                    </div>
                    ) : categories.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                        No categories yet. Click "Add Category" to get started.
                    </div>
                    ) : (
                    categories.map((category) => (
                        <div
                        key={category.id}
                        className="bg-white rounded-xl border overflow-hidden"
                        >
                        {/* Category Header */}
                        <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                            <div
                            className="flex items-center gap-2 cursor-pointer flex-1"
                            onClick={() =>
                                category.id && toggleCategoryExpansion(category.id)
                            }
                            >
                            {expandedCategories.has(category.id || "") ? (
                                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                            ) : (
                                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                            )}
                            <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                            {category.description && (
                                <span className="text-xs text-gray-500 hidden md:inline">
                                - {category.description}
                                </span>
                            )}
                            <span className="inline-block px-2 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded-full">
                                {activityTypesByCategory[category.id || ""]?.length || 0} types
                            </span>
                            </div>

                            {/* Category Actions */}
                            {editingCategory === category.id ? (
                            <div className="flex items-center gap-2">
                                <input
                                className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-36 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                value={categoryForm.name}
                                onChange={(e) =>
                                    setCategoryForm((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                    }))
                                }
                                onClick={(e) => e.stopPropagation()}
                                />
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    category.id && handleUpdateCategory(category.id);
                                }}
                                className="bg-gray-700 text-white px-3 py-1 rounded-md text-xs hover:bg-black"
                                >
                                Save
                                </button>
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingCategory(null);
                                    setCategoryForm({ name: "", description: "" });
                                }}
                                className="bg-gray-200 px-3 py-1 rounded-md text-xs hover:bg-gray-300"
                                >
                                Cancel
                                </button>
                            </div>
                            ) : (
                            <div className="flex items-center gap-2">
                                {(() => {
                                    const filter = subCategoryFilter[category.id || ""] || "all";
                                    const label = filter === "all" ? "All" : filter === "without" ? "Missing sub-cat" : "Has sub-cat";
                                    const colour = filter === "all"
                                        ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                        : filter === "without"
                                        ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                        : "bg-green-100 text-green-700 hover:bg-green-200";
                                    return (
                                        <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (category.id) cycleSubCategoryFilter(category.id);
                                        }}
                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${colour}`}
                                        title="Filter by sub-category status"
                                        >
                                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                        {label}
                                        </button>
                                    );
                                })()}
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (category.id) {
                                    setIsAddingActivityType(category.id);
                                    setEditingActivityType(null);
                                    resetActivityTypeForm();
                                    setExpandedCategories((prev) => {
                                        const newSet = new Set(Array.from(prev));
                                        newSet.add(category.id!);
                                        return newSet;
                                    });
                                    if (!activityTypesByCategory[category.id]) {
                                        fetchActivityTypes(category.id);
                                    }
                                    }
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                                >
                                <PlusIcon className="w-3.5 h-3.5" />
                                Add Activity
                                </button>
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (category.id) {
                                    triggerCsvUpload(category.id);
                                    }
                                }}
                                disabled={isProcessingCsv}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                                CSV
                                </button>
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    startEditingCategory(category);
                                }}
                                className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                title="Edit category"
                                >
                                <PencilIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    category.id && handleDeleteCategory(category.id);
                                }}
                                className="p-1.5 rounded bg-red-100 hover:bg-red-200 text-red-700 transition-colors"
                                title="Delete category"
                                >
                                <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            )}
                        </div>

                        {/* Expanded Content */}
                        {expandedCategories.has(category.id || "") && (
                            <div>
                            {/* Add Activity Type Form (above table) */}
                            {isAddingActivityType === category.id && (
                                <div className="p-4 bg-gray-50 border-b">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Add Activity Type</h4>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Activity Name *
                                        </label>
                                        <input
                                        type="text"
                                        placeholder="e.g., 5K Run, Bench Press"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={activityTypeForm.name}
                                        onChange={(e) =>
                                            setActivityTypeForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                            }))
                                        }
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Image URL (optional)
                                        </label>
                                        <input
                                        type="url"
                                        placeholder="https://..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={activityTypeForm.image}
                                        onChange={(e) =>
                                            setActivityTypeForm((prev) => ({
                                            ...prev,
                                            image: e.target.value,
                                            }))
                                        }
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Video URL (optional)
                                        </label>
                                        <input
                                        type="url"
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={activityTypeForm.videoUrl}
                                        onChange={(e) =>
                                            setActivityTypeForm((prev) => ({
                                            ...prev,
                                            videoUrl: e.target.value,
                                            }))
                                        }
                                        />
                                    </div>
                                    </div>

                                    <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        placeholder="Describe this activity..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                                        rows={2}
                                        value={activityTypeForm.description}
                                        onChange={(e) =>
                                        setActivityTypeForm((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                        }
                                    />
                                    </div>

                                    {(SUB_CATEGORY_OPTIONS[category.name] || []).length > 0 && (
                                    <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-2">
                                        Sub-categories (optional)
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {(SUB_CATEGORY_OPTIONS[category.name] || []).map((option) => {
                                        const checked = activityTypeForm.subCategories.includes(option);
                                        return (
                                            <label
                                            key={option}
                                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-colors ${
                                                checked ? "border-gray-900 bg-white" : "border-gray-200 bg-white hover:bg-gray-50"
                                            }`}
                                            >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() =>
                                                setActivityTypeForm((prev) => ({
                                                    ...prev,
                                                    subCategories: checked
                                                    ? prev.subCategories.filter((s) => s !== option)
                                                    : [...prev.subCategories, option],
                                                }))
                                                }
                                                className="w-3.5 h-3.5 text-black bg-gray-100 border-gray-300 rounded focus:ring-black"
                                            />
                                            {option}
                                            </label>
                                        );
                                        })}
                                    </div>
                                    </div>
                                    )}

                                    <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-2">
                                        Variables *
                                    </label>
                                    <p className="text-xs text-gray-400 mb-3">
                                        Select which metrics users can track for this activity
                                    </p>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                        {PRESET_VARIABLES.map((variableName) => {
                                        const selected = isVariableSelected(variableName);
                                        const availableUnits =
                                            UNIT_OPTIONS[variableName] ||
                                            UNIT_OPTIONS.Default;
                                        const currentUnit = getVariableUnit(variableName);
                                        const currentUnitsList =
                                            getVariableUnitsList(variableName);

                                        return (
                                            <div
                                            key={variableName}
                                            className={`p-3 rounded-lg border-2 transition-colors ${
                                                selected
                                                ? "border-gray-900 bg-white"
                                                : "border-gray-200 bg-white"
                                            }`}
                                            >
                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() =>
                                                    toggleVariable(variableName)
                                                }
                                                className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black"
                                                />
                                                <span className="text-sm font-medium">
                                                {variableName}
                                                </span>
                                            </label>

                                            {selected && (
                                                <div className="mt-2 space-y-2">
                                                <div>
                                                    <label className="text-xs text-gray-600">
                                                    Default Unit:
                                                    </label>
                                                    <select
                                                    value={currentUnit}
                                                    onChange={(e) =>
                                                        updateVariableUnit(
                                                        variableName,
                                                        e.target.value
                                                        )
                                                    }
                                                    className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-black"
                                                    >
                                                    {availableUnits.map((unit) => (
                                                        <option key={unit} value={unit}>
                                                        {unit}
                                                        </option>
                                                    ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-600">
                                                    Available Units:
                                                    </label>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                    {availableUnits.map((unit) => (
                                                        <label
                                                        key={unit}
                                                        className="flex items-center gap-1 text-xs"
                                                        >
                                                        <input
                                                            type="checkbox"
                                                            checked={currentUnitsList.includes(
                                                            unit
                                                            )}
                                                            onChange={(e) => {
                                                            if (e.target.checked) {
                                                                updateVariableUnitsList(
                                                                variableName,
                                                                [...currentUnitsList, unit]
                                                                );
                                                            } else {
                                                                updateVariableUnitsList(
                                                                variableName,
                                                                currentUnitsList.filter(
                                                                    (u) => u !== unit
                                                                )
                                                                );
                                                            }
                                                            }}
                                                            className="w-3 h-3"
                                                        />
                                                        {unit}
                                                        </label>
                                                    ))}
                                                    </div>
                                                </div>
                                                </div>
                                            )}
                                            </div>
                                        );
                                        })}
                                    </div>

                                    {activityTypeForm.variables
                                        .filter(
                                        (v) => !PRESET_VARIABLES.includes(v.name)
                                        )
                                        .map((customVar) => (
                                        <div
                                            key={customVar.name}
                                            className="mb-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50"
                                        >
                                            <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-blue-700">
                                                {customVar.name} (Custom)
                                            </span>
                                            <button
                                                onClick={() =>
                                                toggleVariable(customVar.name)
                                                }
                                                className="p-1 hover:bg-blue-100 rounded"
                                            >
                                                <XMarkIcon className="w-4 h-4 text-blue-600" />
                                            </button>
                                            </div>
                                            <div className="mt-2 text-xs text-blue-600">
                                            Default: {customVar.defaultUnit}
                                            {customVar.unitsList &&
                                                customVar.unitsList.length > 0 && (
                                                <span>
                                                    {" "}
                                                    | Options:{" "}
                                                    {customVar.unitsList.join(", ")}
                                                </span>
                                                )}
                                            </div>
                                        </div>
                                        ))}

                                    {!showCustomVariableInput ? (
                                        <button
                                        type="button"
                                        onClick={() => setShowCustomVariableInput(true)}
                                        className="flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors w-full text-left"
                                        >
                                        <PlusIcon className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600">
                                            Add custom variable
                                        </span>
                                        </button>
                                    ) : (
                                        <div className="p-4 border border-gray-300 rounded-lg bg-white space-y-3">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">
                                            Variable Name
                                            </label>
                                            <input
                                            type="text"
                                            value={customVariableName}
                                            onChange={(e) =>
                                                setCustomVariableName(e.target.value)
                                            }
                                            placeholder="e.g., Cadence, Power"
                                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">
                                            Default Unit
                                            </label>
                                            <input
                                            type="text"
                                            value={customVariableUnit}
                                            onChange={(e) =>
                                                setCustomVariableUnit(e.target.value)
                                            }
                                            placeholder="e.g., bpm, watts"
                                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">
                                            Alternative Units (optional)
                                            </label>
                                            <div className="flex gap-2 mt-1">
                                            <input
                                                type="text"
                                                placeholder="Add unit..."
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                onKeyDown={(e) => {
                                                if (
                                                    e.key === "Enter" &&
                                                    (e.target as HTMLInputElement).value
                                                ) {
                                                    e.preventDefault();
                                                    addUnitToCustomList(
                                                    (e.target as HTMLInputElement).value
                                                    );
                                                    (e.target as HTMLInputElement).value =
                                                    "";
                                                }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                const input = (e.target as HTMLElement)
                                                    .previousElementSibling as HTMLInputElement;
                                                if (input?.value) {
                                                    addUnitToCustomList(input.value);
                                                    input.value = "";
                                                }
                                                }}
                                                className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
                                            >
                                                Add
                                            </button>
                                            </div>
                                            {customVariableUnitsList.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {customVariableUnitsList.map((unit) => (
                                                <span
                                                    key={unit}
                                                    className="px-2 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1"
                                                >
                                                    {unit}
                                                    <button
                                                    onClick={() =>
                                                        removeUnitFromCustomList(unit)
                                                    }
                                                    className="hover:text-red-500"
                                                    >
                                                    <XMarkIcon className="w-3 h-3" />
                                                    </button>
                                                </span>
                                                ))}
                                            </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2 pt-2">
                                            <button
                                            type="button"
                                            onClick={addCustomVariable}
                                            disabled={!customVariableName.trim()}
                                            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-black disabled:opacity-50 transition-colors"
                                            >
                                            Add Variable
                                            </button>
                                            <button
                                            type="button"
                                            onClick={() => {
                                                setShowCustomVariableInput(false);
                                                setCustomVariableName("");
                                                setCustomVariableUnit("units");
                                                setCustomVariableUnitsList([]);
                                            }}
                                            className="px-4 py-2 bg-gray-200 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                                            >
                                            Cancel
                                            </button>
                                        </div>
                                        </div>
                                    )}

                                    {activityTypeForm.variables.length === 0 && (
                                        <p className="text-xs text-red-500 mt-2">
                                        *Please select at least one variable
                                        </p>
                                    )}
                                    </div>
                                </div>

                                <div className="flex justify-end mt-4 items-center gap-3">
                                    <button
                                    onClick={() => {
                                        setIsAddingActivityType(null);
                                        resetActivityTypeForm();
                                    }}
                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                    Cancel
                                    </button>
                                    <button
                                    onClick={() => {
                                        if (category.id) {
                                        handleAddActivityType(category.id);
                                        }
                                    }}
                                    disabled={
                                        !activityTypeForm.name.trim() ||
                                        activityTypeForm.variables.length === 0
                                    }
                                    className="bg-gray-700 text-white py-2 px-4 rounded-md text-sm hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                    Add Activity
                                    </button>
                                </div>
                                </div>
                            )}

                            {/* Activity Types Table */}
                            {activityTypesByCategory[category.id || ""]?.length > 0 ? (
                                <>
                                {/* Table Header */}
                                <div className="grid grid-cols-4 gap-4 px-4 py-2 bg-gray-50/50 border-b text-xs font-medium text-gray-500 uppercase items-center">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={isCategoryAllSelected(category.id || "")}
                                            ref={(el) => {
                                                if (el) el.indeterminate = isCategoryPartiallySelected(category.id || "");
                                            }}
                                            onChange={() => toggleSelectAllInCategory(category.id || "")}
                                            className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black cursor-pointer"
                                        />

                                        Activity Name
                                        </div>
                                    <div>Variables</div>
                                    <div>Description</div>
                                    <div>Actions</div>
                                </div>
                                {/* Table Body */}
                                {getFilteredActivityTypes(category.id || "").map(
                                    (activityType) => (
                                    <div
                                        key={activityType.id}
                                        className={[
                                            "grid grid-cols-4 gap-4 px-4 py-3 border-b items-center transition-colors",
                                            isActivitySelected(category.id || "", activityType.id || "")
                                                ? "bg-blue-50/50 hover:bg-blue-50"
                                                : "hover:bg-gray-50",
                                        ].join(" ")}
                                    >
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={isActivitySelected(category.id || "", activityType.id || "")}
                                                onChange={() => toggleActivitySelection(category.id || "", activityType.id || "")}
                                                className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black cursor-pointer"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {activityType.name}
                                                </span>
                                                {activityType.subCategories && activityType.subCategories.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                    {activityType.subCategories.map((sc) => (
                                                        <span
                                                        key={sc}
                                                        className="inline-block px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full"
                                                        >
                                                        {sc}
                                                        </span>
                                                    ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex overflow-hidden gap-1">
                                            {(activityType.variables as ActivityVariableDefinition[]).map(
                                            (variable, idx) => (
                                                <span
                                                key={idx}
                                                className="inline-block whitespace-nowrap px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full"
                                                >
                                                {typeof variable === "string"
                                                    ? variable
                                                    : `${variable.name} (${variable.defaultUnit})`}
                                                </span>
                                            )
                                            )}
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500 truncate line-clamp-1 block">
                                                {activityType.description || "-"}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                if (category.id) {
                                                    startEditingActivityType(
                                                    category.id,
                                                    activityType
                                                    );
                                                }
                                                }}
                                                className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                                title="Edit activity"
                                            >
                                                <PencilIcon className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                if (category.id && activityType.id) {
                                                    handleDeleteActivityType(
                                                    category.id,
                                                    activityType.id
                                                    );
                                                }
                                                }}
                                                className="p-1.5 rounded bg-red-100 hover:bg-red-200 text-red-700 transition-colors"
                                                title="Delete activity"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    )
                                )}
                                </>
                            ) : (
                                <div className="flex items-center justify-center h-20 text-sm text-gray-400">
                                {subCategoryFilter[category.id || ""] && subCategoryFilter[category.id || ""] !== "all"
                                    ? "No activities match the current filter."
                                    : "No activity types in this category yet."}
                                </div>
                            )}
                            </div>
                        )}
                        </div>
                    ))
                    )}
                </div>
            </div>
        </DashboardWrapper>
    );
};

export default GlobalActivitiesPage;